import {registrationProofContext, registrationProofMessage} from "../util/RegistrationProof";
import {CHAIN} from "../constants/ChainConstants";
import {validateReadSnapshot} from "../util/ChainProtocol";
import {JsonAjaxer} from "../framework/JsonAjaxer";
import {GuildFactory} from "../factories/GuildFactory";
import {PlayerFactory} from "../factories/PlayerFactory";
import {GuildAPIResponseFactory} from "../factories/GuildAPIResponseFactory";
import {GuildAPIError} from "../errors/GuildAPIError";
import {GuildAPICacheItemDTO} from "../dtos/GuildAPICacheItemDTO";
import {InfusionFactory} from "../factories/InfusionFactory";
import {PlayerOreStatsFactory} from "../factories/PlayerOreStatsFactory";
import {PlanetFactory} from "../factories/PlanetFactory";
import {PlayerAddressFactory} from "../factories/PlayerAddressFactory";
import {Guild} from "../models/Guild";
import {ActivationCodeInfoDTO} from "../dtos/ActivationCodeInfoDTO";
import {TransactionFactory} from "../factories/TransactionFactory";
import {PlayerSearchResultDTOFactory} from "../factories/PlayerSearchResultDTOFactory";
import {GuildPowerStatsDTOFactory} from "../factories/GuildPowerStatsDTOFactory";
import {GuildSearchResultDTOFactory} from "../factories/GuildSearchResultDTOFactory";
import {PlanetaryShieldInfoDTOFactory} from "../factories/PlanetaryShieldInfoDTOFactory";
import {PlanetRaidFactory} from "../factories/PlanetRaidFactory";
import {StructTypeFactory} from "../factories/StructTypeFactory";
import {StructFactory} from "../factories/StructFactory";
import {FleetFactory} from "../factories/FleetFactory";
import {WorkFactory} from "../factories/WorkFactory";
import {Struct} from "../models/Struct";
import {SettingFactory} from "../factories/SettingFactory";
import {Settings} from "../models/Settings";
import {Player} from "../models/Player";
import {Infusion} from "../models/Infusion";
import {Fleet} from "../models/Fleet";

export class GuildAPI {

  constructor() {
    this.apiUrl = '/api';
    this.ajax = new JsonAjaxer();

    /** @type {?function(): Promise<boolean>} */
    this.reauthenticator = null;

    /** @type {?Promise<boolean>} The single in-flight session recovery, if any. */
    this.sessionRecovery = null;

    this.guildAPIResponseFactory = new GuildAPIResponseFactory();
    this.guildFactory = new GuildFactory();
    this.playerFactory = new PlayerFactory();
    this.infusionFactory = new InfusionFactory();
    this.playerOreStatsFactory = new PlayerOreStatsFactory();
    this.planetFactory = new PlanetFactory();
    this.playerAddressFactory = new PlayerAddressFactory();
    this.transactionFactory = new TransactionFactory();
    this.playerSearchResultDTOFactory = new PlayerSearchResultDTOFactory();
    this.guildPowerStatsDTOFactory = new GuildPowerStatsDTOFactory();
    this.guildSearchResultDTOFactory = new GuildSearchResultDTOFactory();
    this.planetaryShieldInfoDTOFactory = new PlanetaryShieldInfoDTOFactory();
    this.planetRaidFactory = new PlanetRaidFactory();
    this.structTypeFactory = new StructTypeFactory();
    this.structFactory = new StructFactory();
    this.fleetFactory = new FleetFactory();
    this.workFactory = new WorkFactory();
    this.settingFactory = new SettingFactory();

  }

  /**
   * Install the callback that restores an expired session. Wired at bootstrap
   * rather than constructed here because signing a fresh login needs the
   * wallet, which lives above this layer.
   *
   * @param {function(): Promise<boolean>} reauthenticator
   */
  setReauthenticator(reauthenticator) {
    this.reauthenticator = reauthenticator;
    this.ajax.onUnauthorized = () => this.recoverSession();
  }

  /**
   * Collapses concurrent 401s onto a single login so that a fan-out of parallel
   * requests does not trigger one login per request.
   *
   * @return {Promise<boolean>}
   */
  recoverSession() {
    if (!this.reauthenticator) {
      return Promise.resolve(false);
    }

    if (!this.sessionRecovery) {
      this.sessionRecovery = Promise.resolve()
        .then(() => this.reauthenticator())
        .catch((error) => {
          console.warn('[GuildAPI] session recovery failed:', error);
          return false;
        })
        .finally(() => {
          this.sessionRecovery = null;
        });
    }

    return this.sessionRecovery;
  }

  /**
   * @param {string} key
   * @param {*} value
   */
  cacheItem(key, value) {
    const item = new GuildAPICacheItemDTO(value);
    localStorage.setItem(key, JSON.stringify(item));
  }

  /**
   * @param {string} key
   * @param {number} ttl
   * @return {null|*}
   */
  getCachedItem(key, ttl = 1000 * 60 * 60) {
    let item = localStorage.getItem(key);

    if (item === null) {
      return null;
    }

    item = JSON.parse(item);

    if (item.timestamp + ttl < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  }

  /**
   * @param {string} playerId
   * @param {string} address
   * @param {object} context the registration proof context from getAddressRegistrationProof()
   * @return {string}
   */
  buildAddressRegisterMessage(playerId, address, context) {
    const proof = registrationProofContext(context);
    if (proof.player_id !== playerId || proof.address !== address) {
      throw new Error('Registration proof identity mismatch');
    }
    return registrationProofMessage(proof);
  }

  /**
   * @param {string} playerId
   * @param {string} address
   * @return {Promise<object>} the registration proof context for this player and address
   */
  async getAddressRegistrationProof(playerId, address) {
    const response = this.guildAPIResponseFactory.make(await this.ajax.get(
      `${this.apiUrl}/auth/player-address/${encodeURIComponent(address)}/player/${encodeURIComponent(playerId)}/proof`,
      {timeoutMs: 16000}
    ));
    this.handleResponseFailure(response);
    const proof = registrationProofContext(response.data);
    if (proof.player_id !== playerId || proof.address !== address) {
      throw new Error('Registration proof identity mismatch');
    }
    return proof;
  }

  /**
   * @param {string} guildId
   * @param {string} address
   * @param {number} nonce
   * @return {string}
   */
  buildGuildMembershipJoinProxyMessage(guildId, address, nonce) {
    return `GUILD${guildId}ADDRESS${address}NONCE${nonce}`;
  }

  /**
   *
   * @param {string} guildId
   * @param {string} address
   * @param {string} unixTimestamp
   * @return {string}
   */
  buildLoginMessage(guildId, address, unixTimestamp) {
    return `LOGIN_GUILD${guildId}ADDRESS${address}DATETIME${unixTimestamp}`;
  }

  /**
   * @param {GuildAPIResponse} guildAPIResponse
   */
  handleResponseFailure(guildAPIResponse) {
    if (!guildAPIResponse.success) {
      throw new GuildAPIError(`Guild API request was unsuccessful. See network request for details.`);
    }
  }

  /**
   * @param {string} requestUrl
   * @param {string} dataProperty
   * @return {Promise<*>}
   */
  async getSingleDataValue(requestUrl, dataProperty, options = {}) {
    const jsonResponse = await this.ajax.get(requestUrl, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);

    if (response.data === null
      || response.data === undefined
      || !response.data.hasOwnProperty(dataProperty)
    ) {
      throw new GuildAPIError(`Data does not contain required property (${dataProperty}).`);
    }

    return response.data[dataProperty];
  }

  /**
   * @return {Promise<string[]>}
   */
  async getBannedWords() {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/banned-word/all`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);

    if (!Array.isArray(response.data)) {
      throw new GuildAPIError('Banned word list response was not an array.');
    }

    return response.data
      .filter((row) => row.value)
      .map((row) => String(row.value).toLowerCase());
  }

  /**
   * @return {Promise<Guild>}
   */
  async getThisGuild() {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/this`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.guildFactory.make(response.data);
  }

  /**
   * @return {Promise<string>}
   */
  async getTimestamp() {
    const timestamp = await this.getSingleDataValue(`${this.apiUrl}/timestamp`, 'unix_timestamp');
    return `${timestamp}`;
  }

  /**
   * Read the indexed block and protocol context. Always fetched fresh, so a
   * cached block response never selects the signing rules.
   *
   * @return {Promise<object>}
   */
  async getBlockSnapshot() {
    const response = await this.ajax.get(`${this.apiUrl}/block`);
    if (response.success !== true || !response.data || !response.meta) {
      throw new Error('Indexed protocol state unavailable');
    }
    return response;
  }

  /**
   * @param {SignupRequestDTO} signupRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async signup(signupRequestDTO) {
    const jsonResponse = await this.ajax.post(`${this.apiUrl}/auth/signup`, signupRequestDTO);
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {LoginRequestDTO} loginRequestDTO
   * @return {GuildAPIResponse}
   */
  async login(loginRequestDTO, options = {}) {
    // Login cannot recover itself through the protected-read recovery hook.
    const jsonResponse = await this.ajax.post(`${this.apiUrl}/auth/login`, loginRequestDTO, {...options, reauthenticate: false});
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  async logout(options = {}) {
    const jsonResponse = await this.ajax.post(`${this.apiUrl}/auth/logout`, {}, options);
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {string} playerId
   * @return {Promise<Player>}
   */
  async getPlayer(playerId, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player/${playerId}`, {timeoutMs: 16000, ...options});
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.playerFactory.make(response.data, response.meta);
  }

  /**
   * @param {string} playerId
   * @return {Promise<number>}
   */
  async getPlayerLastActionBlockHeight(playerId) {
    const lastActionBlockHeight = await this.getSingleDataValue(`${this.apiUrl}/player/${playerId}/action/last/block/height`, 'last_action_block_height');
    return parseInt(lastActionBlockHeight);
  }

  /**
   * @param {string} playerId
   * @return {string}
   */
  getPlayerAddressCountCacheKey(playerId) {
    return `getPlayerAddressCount::${playerId}`;
  }

  /**
   * @param {string} playerId
   * @param {boolean} forceRefresh
   * @return {Promise<number>}
   */
  async getPlayerAddressCount(playerId, forceRefresh = false, options = {}) {
    let count = this.getCachedItem(this.getPlayerAddressCountCacheKey(playerId));
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/player-address/count/player/${playerId}`, 'count', options);
      this.cacheItem(this.getPlayerAddressCountCacheKey(playerId), count);
    }
    return parseInt(count);
  }

  /**
   * @param {string} playerId
   * @return {Promise<Infusion>}
   */
  async getInfusionByPlayerId(playerId, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/infusion/player/${playerId}`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.infusionFactory.make(response.data, response.meta);
  }

  /**
   * @param {string} playerId
   * @return {string}
   */
  getPlayerOreStatsCacheKey(playerId) {
    return `getPlayerOreStats::${playerId}`;
  }

  /**
   * @param {string} playerId
   * @param {boolean} forceRefresh
   * @return {Promise<PlayerOreStats>}
   */
  async getPlayerOreStats(playerId, forceRefresh = false) {
    let response = this.getCachedItem(this.getPlayerOreStatsCacheKey(playerId));
    if (response === null || forceRefresh) {
      const jsonResponse = await this.ajax.get(`${this.apiUrl}/player/${playerId}/ore/stats`);
      response = this.guildAPIResponseFactory.make(jsonResponse);
      this.handleResponseFailure(response);
      this.cacheItem(this.getPlayerOreStatsCacheKey(playerId), response);
    }
    return this.playerOreStatsFactory.make(response.data, playerId);
  }

  /**
   * @param {string} playerId
   * @return {string}
   */
  getPlayerPlanetsCompletedCacheKey(playerId) {
    return `getPlayerPlanetsCompleted::${playerId}`;
  }

  /**
   * @param {string} playerId
   * @param {boolean} forceRefresh
   * @return {Promise<number>}
   */
  async getPlayerPlanetsCompleted(playerId, forceRefresh = false) {
    let count = this.getCachedItem(this.getPlayerPlanetsCompletedCacheKey(playerId));
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/player/${playerId}/planet/completed`, 'count');
      this.cacheItem(this.getPlayerPlanetsCompletedCacheKey(playerId), count);
    }
    return parseInt(count);
  }

  /**
   * @param {string} playerId
   * @return {string}
   */
  getPlayerRaidsLaunchedCacheKey(playerId) {
    return `getPlayerRaidsLaunched::${playerId}`;
  }

  /**
   * @param {string} playerId
   * @param {boolean} forceRefresh
   * @return {Promise<number>}
   */
  async getPlayerRaidsLaunched(playerId, forceRefresh = false) {
    let count = this.getCachedItem(this.getPlayerRaidsLaunchedCacheKey(playerId));
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/player/${playerId}/raid/launched`, 'count');
      this.cacheItem(this.getPlayerRaidsLaunchedCacheKey(playerId), count);
    }
    return parseInt(count);
  }

  /**
   * @param {string} planetId
   * @return {Promise<Planet>}
   */
  async getPlanet(planetId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/planet/${planetId}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.planetFactory.make(response.data);
  }

  /**
   * @param {string} planetId
   * @param {string|number} seq
   * @param {string} time
   * @param {object} [options]
   * @return {Promise<object>}
   */
  async getPlanetActivityEvent(planetId, seq, time, options = {}) {
    const query = new URLSearchParams({time});
    const jsonResponse = await this.ajax.get(
      `${this.apiUrl}/planet-activity/planet/${encodeURIComponent(planetId)}/event/${encodeURIComponent(seq)}?${query}`,
      {timeoutMs: 5000, ...options}
    );
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return response.data;
  }

  /**
   * @param {string} playerId
   * @return {Promise<PlayerAddress[]>}
   */
  async getPlayerAddressList(playerId, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player-address/player/${playerId}`, {timeoutMs: 16000, ...options});
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.playerAddressFactory.parseList(response.data);
  }

  /**
   * @param {AddPlayerAddressMetaRequestDTO} addPlayerAddressMetaRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async addPlayerAddressMeta(addPlayerAddressMetaRequestDTO) {
    const jsonResponse = await this.ajax.post(`${this.apiUrl}/player-address/meta`, addPlayerAddressMetaRequestDTO);
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {CreateActivationCodeRequestDTO} createActivationCodeRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async createActivationCode(createActivationCodeRequestDTO) {
    const jsonResponse = await this.ajax.post(`${this.apiUrl}/player-address/activation-code`, createActivationCodeRequestDTO);
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {string} activationCode
   * @return {Promise<ActivationCodeInfoDTO|null>}
   */
  async getActivationCodeInfo(activationCode) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/auth/activation-code/${activationCode}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);

    if (response.data === null) {
      return null;
    }

    const info = new ActivationCodeInfoDTO();
    info.code = activationCode;
    Object.assign(info, response.data);

    return info;
  }

  /**
   * @param {AddPendingAddressRequestDTO} addPendingAddressRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async addPendingAddress(addPendingAddressRequestDTO, options = {}) {
    const jsonResponse = await this.ajax.post(
      `${this.apiUrl}/auth/player-address`,
      addPendingAddressRequestDTO,
      {timeoutMs: 20000, ...options}
    );
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {SetPendingAddressPermissionsRequestDTO} setPendingAddressPermissionsRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async setPendingAddressPermissions(setPendingAddressPermissionsRequestDTO) {
    const jsonResponse = await this.ajax.put(
      `${this.apiUrl}/player-address/pending/permissions`,
      setPendingAddressPermissionsRequestDTO,
      {timeoutMs: 5000}
    );
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {string} activationCode
   * @param {string} activationId
   * @return {Promise<GuildAPIResponse>}
   */
  async deleteActivationCode(activationCode, activationId) {
    const jsonResponse = await this.ajax.delete(
      `${this.apiUrl}/player-address/activation-code/${encodeURIComponent(activationCode)}?activation_id=${encodeURIComponent(activationId)}`,
      undefined,
      {timeoutMs: 5000}
    );
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param address
   * @param guildId
   * @return {Promise<string>}
   */
  async getPlayerIdByAddressAndGuild(address, guildId) {
    return await this.getSingleDataValue(
      `${this.apiUrl}/auth/player-address/${address}/guild/${guildId}/player-id`,
      'player_id'
    );
  }

  /**
   * @param {string} address
   * @param {object} [options]
   * @return {Promise<object>} the raw response, with data and snapshot meta
   */
  async getDeviceState(address, options = {}) {
    const response = await this.ajax.get(
      `${this.apiUrl}/player-address/${encodeURIComponent(address)}/state`,
      {timeoutMs: 16000, ...options}
    );
    if (response.success !== true || !response.meta || !response.data) {
      throw new Error('Device state is unavailable');
    }
    return response;
  }

  /**
   * @param {string} playerId
   * @param {string} address
   * @param {object} [options]
   * @return {Promise<object>} the raw response, with data and snapshot meta
   */
  async getRegistrationStatus(playerId, address, options = {}) {
    const response = await this.ajax.get(
      `${this.apiUrl}/auth/player-address/${encodeURIComponent(address)}/player/${encodeURIComponent(playerId)}/registration`,
      {timeoutMs: 16000, ...options}
    );
    if (response.success !== true || !response.meta || !response.data) {
      throw new Error('Registration status is unavailable');
    }
    return response;
  }

  /**
   * @param {string} code
   * @param {string} activationId
   * @param {object} [options]
   * @return {Promise<object>}
   */
  async getPendingAddressByCode(code, activationId, options = {}) {
    const response = this.guildAPIResponseFactory.make(await this.ajax.get(
      `${this.apiUrl}/player-address/code/${encodeURIComponent(code)}?activation_id=${encodeURIComponent(activationId)}`,
      {timeoutMs: 5000, ...options}
    ));
    this.handleResponseFailure(response);
    return response.data;
  }

  /**
   * @param {string} address
   * @param {object} [options]
   * @return {Promise<PlayerAddress|null>}
   */
  async getPlayerAddress(address, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player-address/${address}`, {timeoutMs: 16000, ...options});
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return response.data === null ? null : this.playerAddressFactory.make(response.data);
  }

  /**
   * @param {SetAddressPermissionsRequestDTO} setAddressPermissionsRequestDTO
   * @return {Promise<GuildAPIResponse>}
   */
  async setAddressPermissions(setAddressPermissionsRequestDTO) {
    const jsonResponse = await this.ajax.put(`${this.apiUrl}/player-address/permissions`, setAddressPermissionsRequestDTO);
    return this.guildAPIResponseFactory.make(jsonResponse);
  }

  /**
   * @param {string} playerId
   * @param {number} page
   * @return {Promise<Transaction[]>}
   */
  async getTransactions(playerId, page, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/ledger/player/${playerId}/page/${page}`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    const transactions = this.transactionFactory.parseList(response.data);
    transactions.snapshot_meta = response.meta;
    for (const transaction of transactions) {
      transaction.snapshot_meta = response.meta;
    }
    return transactions;
  }

  /**
   * @param {string} playerId
   * @return {Promise<number>}
   */
  async countTransactions(playerId, options = {}) {
    const count = await this.getSingleDataValue(`${this.apiUrl}/ledger/player/${playerId}/count`, 'count', options);
    return parseInt(count);
  }

  /**
   * @param {number} txId
   * @return {Promise<Transaction>}
   */
  async getTransaction(txId, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/ledger/${txId}`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    const transaction = this.transactionFactory.make(response.data);
    transaction.snapshot_meta = response.meta;
    return transaction;
  }

  /**
   * Find the ledger entry for a committed transfer. The source fields come from
   * the verified committed result and the exact transfer intent.
   *
   * @param {string} playerId
   * @param {object} source
   * @param {object} [options]
   * @return {Promise<GuildAPIResponse>}
   */
  async getTransferSource(playerId, source, options = {}) {
    const query = new URLSearchParams(source);
    const jsonResponse = await this.ajax.get(
      `${this.apiUrl}/ledger/player/${encodeURIComponent(playerId)}/transfer-source?${query}`,
      options
    );
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    if (response.data) {
      response.data = this.transactionFactory.make(response.data);
      response.data.snapshot_meta = response.meta;
    }
    return response;
  }

  /**
   * @return {Promise<Guild[]>}
   */
  async getGuildFilterList(options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/name`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.guildFactory.parseList(response.data);
  }

  /**
   * @param transferSearchRequestDTO
   * @return {Promise<PlayerSearchResultDTO[]>}
   */
  async transferSearch(transferSearchRequestDTO, options = {}) {
    const searchStringParam = `search_string=${transferSearchRequestDTO.search_string}`;
    const guildIdParam = transferSearchRequestDTO.guild_id ? `&guild_id=${transferSearchRequestDTO.guild_id}` : '';
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player/transfer/search?${searchStringParam}${guildIdParam}`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    const players = this.playerSearchResultDTOFactory.parseList(response.data);
    for (const player of players) {
      player.snapshot_meta = response.meta;
    }
    return players;
  }

  /**
   * @return {string}
   */
  getCountGuildMembersCacheKey(guildId) {
    return `countGuildMembers::${guildId}`;
  }

  /**
   * @param {string} guildId
   * @param {boolean} forceRefresh
   * @return {Promise<number>}
   */
  async countGuildMembers(guildId, forceRefresh = false) {
    let count = this.getCachedItem(this.getCountGuildMembersCacheKey(guildId));
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/guild/${guildId}/members/count`, 'count');
      this.cacheItem(this.getCountGuildMembersCacheKey(guildId), count);
    }
    return parseInt(count);
  }

  /**
   * @return {string}
   */
  getCountGuildsCacheKey() {
    return `countGuilds`;
  }

  /**
   * @return {Promise<number>}
   */
  async countGuilds(forceRefresh = false) {
    let count = this.getCachedItem(this.getCountGuildsCacheKey());
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/guild/count`, 'count');
      this.cacheItem(this.getCountGuildsCacheKey(), count);
    }
    return parseInt(count);
  }

  /**
   * @param {string} guildId
   * @return {Promise<Guild>}
   */
  async getGuild(guildId, options = {}) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/${guildId}`, options);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.guildFactory.make(response.data);
  }

  /**
   * @param {string} guildId
   * @return {string}
   */
  getGuildPowerStatsCacheKey(guildId) {
    return `getGuildPowerStats::${guildId}`;
  }

  /**
   * @param {string} guildId
   * @param {boolean} forceRefresh
   * @return {Promise<GuildPowerStatsDTO>}
   */
  async getGuildPowerStats(guildId, forceRefresh = false) {
    let stats = this.getCachedItem(this.getGuildPowerStatsCacheKey(guildId));
    if (stats === null || forceRefresh) {
      const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/${guildId}/power/stats`);
      const response = this.guildAPIResponseFactory.make(jsonResponse);
      this.handleResponseFailure(response);
      stats = this.guildPowerStatsDTOFactory.make(response.data);
      this.cacheItem(this.getGuildPowerStatsCacheKey(guildId), stats);
    }
    return stats;
  }

  /**
   * @param {string} guildId
   * @return {string}
   */
  countGuildPlanetsCompletedCacheKey(guildId) {
    return `countGuildPlanetsCompleted::${guildId}`;
  }

  /**
   * @return {Promise<number>}
   */
  async countGuildPlanetsCompleted(guildId, forceRefresh = false) {
    let count = this.getCachedItem(this.countGuildPlanetsCompletedCacheKey(guildId));
    if (count === null || forceRefresh) {
      count = await this.getSingleDataValue(`${this.apiUrl}/guild/${guildId}/planet/complete/count`, 'count');
      this.cacheItem(this.countGuildPlanetsCompletedCacheKey(guildId), count);
    }
    return parseInt(count);
  }

  /**
   * @param {string} guildId
   * @return {Promise<PlayerSearchResultDTO[]>}
   */
  async getGuildRoster(guildId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/${guildId}/roster`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.playerSearchResultDTOFactory.parseList(response.data);
  }

  /**
   * @return {string}
   */
  getGuildsDirectoryCacheKey() {
    return `getGuildsDirectory`;
  }

  /**
   * @param {boolean} forceRefresh
   * @return {Promise<GuildSearchResultDTO[]>}
   */
  async getGuildsDirectory(forceRefresh = false) {
    let guilds = this.getCachedItem(this.getGuildsDirectoryCacheKey());
    if (guilds === null || forceRefresh) {
      const jsonResponse = await this.ajax.get(`${this.apiUrl}/guild/directory`);
      const response = this.guildAPIResponseFactory.make(jsonResponse);
      this.handleResponseFailure(response);
      guilds = this.guildSearchResultDTOFactory.parseList(response.data);
      this.cacheItem(this.getGuildsDirectoryCacheKey(), guilds);
    }
    return guilds;
  }

  /**
   * @param {string} planetId
   * @return {Promise<PlanetaryShieldInfoDTO>}
   */
  async getPlanetaryShieldInfo(planetId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/planet/${planetId}/shield`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.planetaryShieldInfoDTOFactory.make(response.data);
  }

  /**
   * @param {RaidSearchRequestDTO} raidSearchRequestDTO
   * @return {Promise<PlayerSearchResultDTO[]>}
   */
  async raidSearch(raidSearchRequestDTO) {
    const searchStringParam = raidSearchRequestDTO.search_string ? `search_string=${raidSearchRequestDTO.search_string}` : '';
    const guildIdParam = raidSearchRequestDTO.guild_id ? `&guild_id=${raidSearchRequestDTO.guild_id}` : '';
    const minOreParam = `&min_ore=${raidSearchRequestDTO.min_ore}`;
    const fleetAwayOnlyParam = `&fleet_away_only=${raidSearchRequestDTO.fleet_away_only ? 1 : 0}`;
    const notUnderAttackParam = `&not_under_attack=${raidSearchRequestDTO.not_under_attack ? 1 : 0}`;
    const pageParam = `&page=${raidSearchRequestDTO.page}`;

    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player/raid/search?${searchStringParam}${guildIdParam}${minOreParam}${fleetAwayOnlyParam}${notUnderAttackParam}${pageParam}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.playerSearchResultDTOFactory.parseList(response.data);
  }

  /**
   * @param {RaidSearchRequestDTO} raidSearchRequestDTO
   * @return {Promise<number>}
   */
  async raidSearchCount(raidSearchRequestDTO) {
    const count_only = `count_only=1`;
    const searchStringParam = raidSearchRequestDTO.search_string ? `&search_string=${raidSearchRequestDTO.search_string}` : '';
    const guildIdParam = raidSearchRequestDTO.guild_id ? `&guild_id=${raidSearchRequestDTO.guild_id}` : '';
    const minOreParam = `&min_ore=${raidSearchRequestDTO.min_ore}`;
    const fleetAwayOnlyParam = `&fleet_away_only=${raidSearchRequestDTO.fleet_away_only ? 1 : 0}`;
    const notUnderAttackParam = `&not_under_attack=${raidSearchRequestDTO.not_under_attack ? 1 : 0}`;

    const jsonResponse = await this.ajax.get(`${this.apiUrl}/player/raid/search?${count_only}${searchStringParam}${guildIdParam}${minOreParam}${fleetAwayOnlyParam}${notUnderAttackParam}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);

    return (response.data.hasOwnProperty('length') && response.data.length === 1 && response.data[0].hasOwnProperty('count'))
      ? parseInt(response.data[0].count)
      : 0;
  }

  /**
   * @param {string} planetId
   * @return {Promise<PlanetRaid>}
   */
  async getActivePlanetRaidByPlanetId(planetId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/planet/${planetId}/raid/active`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.planetRaidFactory.make(response.data);
  }

  /**
   * @param {string} fleetId
   * @return {Promise<PlanetRaid>}
   */
  async getActivePlanetRaidByFleetId(fleetId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/planet/raid/active/fleet/${fleetId}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.planetRaidFactory.make(response.data);
  }

  /**
   * @return {string}
   */
  getStructTypesCacheKey() {
    return `getStructTypes`;
  }

  /**
   * @return {Promise<StructType[]>}
   */
  async getStructTypes() {
    // The catalog is not cached: a cached copy would have no source height or
    // protocol identity, and a fresh one is small. Only concurrent reads share
    // one network request.
    if (!this.structTypesRequest) {
      this.structTypesRequest = this.ajax.get(`${this.apiUrl}/struct/type`, {timeoutMs: 15000})
        .then(jsonResponse => {
          const response = this.guildAPIResponseFactory.make(jsonResponse);
          this.handleResponseFailure(response);
          validateReadSnapshot(response.meta, {chainId: CHAIN.ID, height: response.meta?.observed_tip});
          const types = this.structTypeFactory.parseList(response.data);
          types.snapshot_meta = response.meta;
          return types;
        })
        .finally(() => {
          this.structTypesRequest = null;
        });
    }
    return this.structTypesRequest;
  }

  /**
   * @param {string} playerId
   * @return {Promise<Struct[]>}
   */
  async getStructsByPlayerId(playerId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/struct/player/${playerId}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return response.data.map(row => this.structFactory.make(row, response.meta));
  }

  /**
   * @param {string} playerId
   * @return {Promise<Fleet>}
   */
  async getFleetByPlayerId(playerId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/fleet/player/${playerId}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.fleetFactory.make(response.data);
  }

  /**
   * @param playerId
   * @return {Promise<Work[]>}
   */
  async getWorkByPlayerId(playerId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/work/player/${playerId}`, {timeoutMs: 15000});
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    validateReadSnapshot(response.meta, {chainId: CHAIN.ID, height: response.meta?.observed_tip}, ['work']);
    const work = this.workFactory.parseList(response.data);
    const identities = new Set();
    for (const item of work) {
      if (item.player_id !== playerId || identities.has(item.object_id)) {
        throw new Error('Work owner or identity is invalid');
      }
      identities.add(item.object_id);
    }
    // An empty but complete snapshot still carries authority, e.g. to stop work.
    work.snapshot_meta = response.meta;
    for (const item of work) {
      item.snapshot_meta = response.meta;
    }
    return work;
  }

  /**
   * @param {string} structId
   * @return {Promise<Struct>}
   */
  async getStruct(structId) {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/struct/${structId}`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.structFactory.make(response.data, response.meta);
  }

  /**
   * The exact stored generator output. sourceAddress only selects what to
   * read; it grants no authority.
   *
   * @param {string} structId
   * @param {string} sourceAddress
   * @param {object} [options]
   * @return {Promise<object|null>}
   */
  async getGeneratorSnapshot(structId, sourceAddress, options = {}) {
    const response = await this.getGeneratorSnapshotResult(structId, sourceAddress, options);
    return response.data;
  }

  /**
   * Like getGeneratorSnapshot(), but returns the whole response so the
   * snapshot metadata is kept even when the struct has since been removed.
   *
   * @param {string} structId
   * @param {string} sourceAddress
   * @param {object} [options]
   * @return {Promise<GuildAPIResponse>}
   */
  async getGeneratorSnapshotResult(structId, sourceAddress, options = {}) {
    const query = new URLSearchParams({address: sourceAddress});
    const jsonResponse = await this.ajax.get(
      `${this.apiUrl}/struct/${encodeURIComponent(structId)}/generator?${query}`,
      options
    );
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    if (response.data !== null) {
      response.data = {...response.data, snapshot_meta: response.meta};
    }
    return response;
  }

  /**
   * @return {Promise<Settings>}
   */
  async getSettings() {
    const jsonResponse = await this.ajax.get(`${this.apiUrl}/setting`);
    const response = this.guildAPIResponseFactory.make(jsonResponse);
    this.handleResponseFailure(response);
    return this.settingFactory.parseList(response.data);
  }
}
